import type { CheckoutSkiAncillaryDuration, CheckoutSkiAncillarySelectedValue, CheckoutSkiAncillaryService } from '../../../domain/types'
import { differenceInCalendarDays, parseISO } from 'date-fns'
import { NoSportType } from '@/scopes/checkout/infrastructure/api/service-mappers/skiAncillaryServiceMapper'

export const skiAncillaryServiceDurationsSelector: (service: CheckoutSkiAncillaryService) => CheckoutSkiAncillaryDuration[]
  = ({ durations }) => Object.values(durations)

export const skiAncillaryServiceHasIncludedOptionSelector: (service: CheckoutSkiAncillaryService) => boolean
  = ({ options }) => Object.values(options).some(({ isIncluded }) => isIncluded)

export const skiAncillarySportTypeSelector: (service: CheckoutSkiAncillaryService) => string[]
  = ({ options }) => {
    return Array.from(new Set(Object.values(options).map(option => option.sportType ?? '').sort((a, b) => {
      if (a === NoSportType)
        return 1
      if (b === NoSportType)
        return -1
      return a.localeCompare(b)
    })))
  }

export const skiAncillaryLevelSelector: (service: CheckoutSkiAncillaryService, sportType: string, participantId?: string) => string[]
  = ({ options, participants }, sportType, participantId) => {
    const availableOptions = (participantId && participants[participantId]?.availableOptions) || []

    const levels = Object.values(options).filter(
      option =>
        option.sportType === sportType
        && (!participantId || availableOptions.length === 0 || availableOptions.includes(option.id)),
    ).map(option => option.level)
    return Array.from(new Set(levels))
  }

export const skiAncillaryServiceEditingValueByIdSelector: (service: CheckoutSkiAncillaryService, participantId: string) => CheckoutSkiAncillarySelectedValue
  = ({ editingSelectedValues }, participantId) => {
    return editingSelectedValues[participantId]
  }

export const skiAncillaryDaysRangeByDurationSelector: (
  service: CheckoutSkiAncillaryService,
  durationId: string,
  sportType?: string
) => { start: string, end: string }[] = ({ options, isNoneOption }, durationId, sportType) => {
  const uniqueDateRanges = new Set<string>()
  const expectedDays = Number.parseInt(durationId, 10)

  return Object.values(options)
    .filter(option => option.duration === durationId)
    .filter(option => (sportType ? option.sportType === sportType : option.id !== isNoneOption))
    .map(option => ({ start: option.startDate, end: option.endDate }))
    .filter(({ start, end }) => {
      const startDate = parseISO(start)
      const endDate = parseISO(end)
      const dayCount = differenceInCalendarDays(endDate, startDate) + 1 // Include both start and end dates
      return dayCount === expectedDays
    })
    .filter(({ start, end }) => {
      const key = `${start}-${end}`
      if (uniqueDateRanges.has(key)) {
        return false
      }
      uniqueDateRanges.add(key)
      return true
    })
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()) // Sort by start date
}

export const skiAncillaryServiceOptionsListSelector: (
  service: CheckoutSkiAncillaryService,
  durationId: string,
  start: string,
  sportType?: string,
  level?: string
) => string[] = ({ durations, options }, durationId, start, sportType, level) => {
  const availableOptionsForDuration = durations[durationId]?.availableOptions

  return availableOptionsForDuration?.map(optionId => options[optionId])
    .filter(option => option.duration === durationId)
    .filter(option => option.startDate === start)
    .filter(option => (sportType ? option.sportType === sportType : true))
    .filter(option => (level ? option.level === level : true))
    .map(option => option.id) ?? []
}
